// Type definitions for the ROQ AR Demo application

/**
 * Application state enumeration defining all possible states
 * Based on requirements 1.1, 2.1, 3.1, 4.1, 5.1, 6.1, 7.1
 */
export enum AppStateType {
  LOADING = 'loading',
  PERMISSION_REQUEST = 'permission_request',
  CLIENT_SELECTION = 'client_selection',
  AR_SCANNING = 'ar_scanning',
  AR_TRACKING = 'ar_tracking',
  AR_COMPLETE = 'ar_complete',
  ERROR = 'error'
}

/**
 * Permission state interface for tracking individual permissions
 * Requirements 2.1-2.5 specify both camera and location permissions are required
 */
export interface PermissionState {
  camera: boolean;
  location: boolean;
}

/**
 * Main application state interface
 * Tracks all critical application state for proper flow control
 */
export interface AppState {
  currentState: AppStateType;
  assetsLoaded: boolean;
  consentGiven: boolean;
  permissions: PermissionState;
  markerTracking: boolean;
  animationComplete: boolean;
  scanningStartTime?: number;
  errorMessage?: string;
  lastStateChange: number;
}

/**
 * Analytics event structure for Firebase Analytics integration
 * Supports all required tracking events from requirements
 */
export interface AnalyticsEvent {
  eventName: string;
  timestamp: number;
  userData?: UserData;
  customData?: Record<string, any>;
}

/**
 * User data collection interface for analytics
 * Includes location, device, and browser information
 */
export interface UserData {
  location?: GeolocationData;
  deviceInfo: DeviceInfo;
  browserInfo: BrowserInfo;
}

/**
 * Geolocation data for analytics tracking
 * Optional fields to handle permission scenarios
 */
export interface GeolocationData {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  timestamp?: number;
}

/**
 * Device information for analytics
 * Captures essential device characteristics
 */
export interface DeviceInfo {
  userAgent: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  touchSupport: boolean;
}

/**
 * Browser information for compatibility tracking
 * Helps identify browser-specific issues
 */
export interface BrowserInfo {
  name: string;
  version: string;
  language: string;
  webglSupport: boolean;
  webrtcSupport: boolean;
}

/**
 * Firebase configuration interface
 * Required for Firebase Analytics initialization
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

/**
 * Per-character configuration for multi-target collections
 */
export interface CharacterConfig {
  targetIndex: number;
  characterId: string;
  name: string;
  videoUrl: string;
  /** Alternative video URL used on iOS Safari, which does not support WebM.
   *  Typically a .mov or .mp4 file. Falls back to videoUrl if not set. */
  iosVideoUrl?: string;
  /** Alternative video URL used on iOS Chrome (CriOS), which may not render
   *  WebM with transparency correctly. Typically a .mp4 file. Falls back to
   *  iosVideoUrl then videoUrl if not set. */
  chromeIOSVideoUrl?: string;
  audioUrl?: string;        // Optional separate background music
  thumbnail: string;
  ctaUrl?: string;
  ctaText?: string;
  description?: string;
  /** Per-character compiled .mind file. When set, selecting this character
   *  reinitialises MindAR with this single-target file so its specific pin
   *  is detected rather than relying on a shared multi-target file. */
  mindFile?: string;
}

/**
 * Asset configuration for preloading
 * Defines all assets required for the AR experience
 */
export interface AssetConfig {
  arLibraryUrl: string;
  mindARLibraryUrl: string;  // MindAR library URL
  imageTargetsUrl: string;   // Compiled .mind file containing image targets
  roqLogoUrl: string;        // ROQ logo image for animation
  videoUrl: string;          // Default video file (used for single-target clients)
  animationModelUrl: string;
  preloadTimeout: number;
  targetPhysicalWidth?: number; // Physical width of printed target in mm
  characters?: CharacterConfig[]; // Per-character config for multi-target collections
}

/**
 * A-Frame position interface for 3D positioning
 * Used for CTA placement and 3D object positioning
 */
export interface AFramePosition {
  x: number;
  y: number;
  z: number;
}

/**
 * State change event interface
 * Used by event emitter for state transition notifications
 */
export interface StateChangeEvent {
  previousState: AppStateType;
  newState: AppStateType;
  timestamp: number;
  data?: Record<string, any>;
}

/**
 * Error types for better error handling
 * Categorizes different types of errors that can occur
 */
export enum ErrorType {
  CAMERA_PERMISSION_DENIED = 'camera_permission_denied',
  LOCATION_PERMISSION_DENIED = 'location_permission_denied',
  PERMISSIONS_DENIED = 'permissions_denied',
  ASSET_LOAD_FAILED = 'asset_load_failed',
  AR_INITIALIZATION_FAILED = 'ar_initialization_failed',
  WEBGL_NOT_SUPPORTED = 'webgl_not_supported',
  WEBRTC_NOT_SUPPORTED = 'webrtc_not_supported',
  UNKNOWN_ERROR = 'unknown_error'
}

/**
 * Application error interface
 * Provides structured error information
 */
export interface AppError {
  type: ErrorType;
  message: string;
  timestamp: number;
  recoverable: boolean;
  details?: Record<string, any>;
}